import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Trash2, Loader2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'

interface Subject {
  id: string
  name: string
  color?: string
}

interface SubjectsConfigurationProps {
  subjects: Subject[]
  isLoading?: boolean
  onAddSubject: (subject: Subject) => void
  onDeleteSubject: (subjectId: string) => void
  refetchSubjects?: () => Promise<any>
}

export function SubjectsConfiguration({
  subjects,
  isLoading = false,
  onAddSubject,
  onDeleteSubject,
  refetchSubjects,
}: SubjectsConfigurationProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.subjectsConfiguration' })
  // const { toast } = useToast()
  // const [newSubjectName, setNewSubjectName] = useState('')
  // const [newSubjectColor, setNewSubjectColor] = useState(getRandomColor())

  // Function to generate a random color
  function getRandomColor() {
    const colors = [
      '#FF5733',
      '#33FF57',
      '#3357FF',
      '#F033FF',
      '#FF33F0',
      '#33FFF0',
      '#F0FF33',
      '#FF3333',
      '#33FF33',
      '#3333FF',
      '#FFAA33',
      '#33FFAA',
      '#AA33FF',
      '#FF33AA',
      '#33AAFF',
    ]
    return colors[Math.floor(Math.random() * colors.length)]
  }

  // Check for invalid data and trigger refetch if needed
  useEffect(() => {
    // Only check after loading is complete
    if (!isLoading && refetchSubjects) {
      // Check if we have subjects but they're invalid
      const hasSubjects = subjects && subjects.length > 0
      const invalidSubjects = hasSubjects 
        ? subjects.filter(s => !s || !s.id || !s.name || s.name.trim() === '')
        : []
      
      // If we have invalid subjects (all subjects are invalid or some are invalid)
      if (invalidSubjects.length > 0) {
        console.warn('⚠️ SubjectsConfiguration: Invalid data detected, refetching...', {
          invalidCount: invalidSubjects.length,
          totalCount: subjects?.length || 0
        })
        
        // Use refetch function with a small delay to avoid race conditions
        const timeoutId = setTimeout(() => {
          refetchSubjects().catch((error) => {
            console.error('Failed to refetch subjects:', error)
            // Fallback to reload if refetch fails after retry
            setTimeout(() => {
              if (refetchSubjects) {
                refetchSubjects().catch(() => {
                  window.location.reload()
                })
              } else {
                window.location.reload()
              }
            }, 2000)
          })
        }, 500)
        
        return () => clearTimeout(timeoutId)
      }
    }
  }, [subjects, isLoading, refetchSubjects])

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin mr-2" />
            <p>{t('loading')}</p>
          </div>
        ) : subjects.length === 0 ? (
          <div className="text-center p-4 border rounded-md bg-muted/20">
            {t('empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('table.subjectName')}</TableHead>
                  <TableHead>{t('table.color')}</TableHead>
                  <TableHead className="w-[100px]">{t('table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subjects
                  .filter((subject) => subject && subject.id && subject.name && subject.name.trim() !== '')
                  .map((subject) => (
                    <TableRow key={subject.id}>
                      <TableCell>{subject.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-full border border-gray-300 cursor-pointer"
                            style={{
                              backgroundColor: subject.color || getRandomColor(),
                            }}
                            onClick={() => {
                              // Create a temporary input element to handle color picking
                              const input = document.createElement('input')
                              input.type = 'color'
                              input.value = subject.color || getRandomColor()
                              input.addEventListener('change', (e) => {
                                // Update the subject color
                                const newColor = (e.target as HTMLInputElement)
                                  .value
                                const updatedSubject = {
                                  ...subject,
                                  color: newColor,
                                }
                                // Remove the old subject and add the updated one
                                onDeleteSubject(subject.id)
                                onAddSubject(updatedSubject)
                              })
                              input.click()
                            }}
                          />
                          <span className="text-xs text-gray-500">
                            {(subject.color || getRandomColor()).toUpperCase()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteSubject(subject.id)}
                          title={t('deleteTitle')}
                          className="min-h-[44px] min-w-[44px]"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            <div className="mt-4 text-sm text-muted-foreground">
              {t('total', { count: subjects.length })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
